import { DndContext, DragOverlay, type DragStartEvent } from "@dnd-kit/core";
import { useMemo, useState } from "react";
import type { Item, Project } from "../api/types";
import { useT } from "../i18n";
import { keyBetween, rootTasks, sectionsOf } from "../lib/tasks";
import { useSyncState } from "../store";
import { updateProject, updateSection } from "../sync/actions";
import { type Container, collision, DroppableList, handleTaskDrop, SortableItem, useDndSensors } from "../tasks/dnd";
import { TaskRow } from "../tasks/TaskRow";
import { AddTask, EmptyState, TaskTree } from "../tasks/TaskTree";
import { usePrefs } from "../ui/prefs";
import { BoardView } from "./BoardView";
import { CalendarView } from "./CalendarView";
import { applySort, groupTasks, useCompletedTasks } from "./grouping";
import { ProjectMenuButton } from "./ProjectMenu";
import { AddSection, SectionHeader } from "./sections";
import { useViewOptions, ViewOptionsButton } from "./viewOptions";

function EditableTitle({ project }: { project: Project }) {
  const t = useT();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(project.name);
  const title = project.inbox_project ? t("inbox") : project.name;
  if (editing && !project.inbox_project) {
    return (
      <input
        className="input view-title"
        style={{ height: 40, fontSize: 22 }}
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => {
          setEditing(false);
          if (name.trim() && name.trim() !== project.name) void updateProject(project.id, { name: name.trim() });
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          if (e.key === "Escape") {
            e.stopPropagation();
            setName(project.name);
            setEditing(false);
          }
        }}
      />
    );
  }
  return (
    <h1
      className="view-title"
      onClick={() => {
        setName(project.name);
        setEditing(true);
      }}
    >
      {title}
    </h1>
  );
}

export function ProjectView({ projectId }: { projectId: string }) {
  const state = useSyncState();
  const t = useT();
  const project = state.projects[projectId];
  const o = useViewOptions("project", projectId);
  const completed = useCompletedTasks(o.showCompleted ? { project_id: projectId } : null);

  if (!project) return <EmptyState text={t("emptyProject")} />;
  return (
    <div className={`view${o.mode === "board" ? " wide" : ""}`} data-testid="project-view" data-view-mode={o.mode}>
      <div className="view-header" style={o.mode === "board" ? { padding: "4px 24px 8px" } : undefined}>
        <EditableTitle project={project} />
        <div style={{ display: "flex", gap: 4 }}>
          <ViewOptionsButton
            viewType="project"
            objectId={projectId}
            groupings={["none", "due_date", "added_date", "priority", "label"]}
          />
          {!project.inbox_project && <ProjectMenuButton project={project} />}
        </div>
      </div>
      {o.mode === "board" ? (
        <BoardView project={project} options={o} completed={completed} />
      ) : o.mode === "calendar" ? (
        <CalendarView
          tasks={Object.values(state.items).filter((i) => i.project_id === projectId && !i.checked)}
          layout={o.calendarLayout}
          viewType="project"
          objectId={projectId}
          addDefaults={{ projectId }}
        />
      ) : (
        <ProjectList project={project} completed={completed} />
      )}
    </div>
  );
}

function ProjectList({ project, completed }: { project: Project; completed: Item[] }) {
  const state = useSyncState();
  const prefs = usePrefs();
  const t = useT();
  const o = useViewOptions("project", project.id);
  const sensors = useDndSensors();
  const [dragging, setDragging] = useState<Item | null>(null);
  const sections = sectionsOf(state, project.id);
  const manual = !o.sortedBy || o.sortedBy === "manual";

  const containers: Container[] = useMemo(
    () => [
      {
        id: "none",
        tasks: applySort(rootTasks(state, project.id, null), o, prefs.tz),
        target: { project_id: project.id, section_id: null, parent_id: null },
      },
      ...sectionsOf(state, project.id).map((s) => ({
        id: s.id,
        tasks: applySort(rootTasks(state, project.id, s.id), o, prefs.tz),
        target: { project_id: project.id, section_id: s.id, parent_id: null },
      })),
    ],
    [state, project.id, o, prefs.tz],
  );

  const completedFor = (sectionId: string | null) =>
    completed
      .filter((c) => (c.section_id ?? null) === sectionId && !c.parent_id)
      .sort((a, b) => ((b.completed_at ?? "") < (a.completed_at ?? "") ? -1 : 1));

  if (o.groupedBy) {
    const all = applySort(
      Object.values(state.items).filter((i) => i.project_id === project.id && !i.checked && !i.parent_id),
      o,
      prefs.tz,
    );
    const groups = groupTasks(all, o.groupedBy, { state, tz: prefs.tz, today: prefs.today, lang: prefs.lang }) ?? [];
    return (
      <div role="list">
        {groups.map((g) => (
          <div className="section" key={g.key}>
            <SectionHeader title={g.title} count={g.tasks.length} />
            {g.tasks.map((it) => (
              <TaskTree key={it.id} item={it} />
            ))}
          </div>
        ))}
        {!groups.length && <EmptyState text={project.inbox_project ? t("emptyInbox") : t("emptyProject")} />}
        <AddTask defaults={{ projectId: project.id }} />
      </div>
    );
  }

  const lastKey = sections.at(-1);
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collision}
      onDragStart={(e: DragStartEvent) => setDragging(state.items[String(e.active.id)] ?? null)}
      onDragCancel={() => setDragging(null)}
      onDragEnd={(e) => {
        setDragging(null);
        if (manual) handleTaskDrop(e, containers);
      }}
    >
      {containers.map((c, idx) => {
        const section = c.id === "none" ? undefined : state.sections[c.id];
        const collapsed = section?.is_collapsed ?? false;
        const done = completedFor(section?.id ?? null);
        return (
          <div
            className={section ? "section" : ""}
            key={c.id}
            data-testid={section ? "section" : "no-section"}
            data-section-name={section?.name}
          >
            {section && (
              <SectionHeader
                section={section}
                title={section.name}
                count={c.tasks.length}
                collapsed={collapsed}
                onToggle={() => void updateSection(section.id, { is_collapsed: !collapsed })}
              />
            )}
            {!collapsed && (
              <>
                <DroppableList id={c.id} items={c.tasks.map((x) => x.id)}>
                  <div role="list">
                    {c.tasks.map((it) => (
                      <SortableItem key={it.id} id={it.id}>
                        {(handle, isDragging) => (
                          <TaskTree
                            item={it}
                            dragHandle={manual ? handle : null}
                            dragging={isDragging}
                            showCompleted={o.showCompleted}
                          />
                        )}
                      </SortableItem>
                    ))}
                    {done.map((it) => (
                      <TaskRow key={it.id} item={it} />
                    ))}
                  </div>
                </DroppableList>
                <AddTask defaults={{ projectId: project.id, sectionId: section?.id ?? null }} />
              </>
            )}
            {idx < containers.length - 1 && (
              <AddSection
                projectId={project.id}
                orderKey={keyBetween(section, section ? sections[sections.indexOf(section) + 1] : sections[0])}
              />
            )}
          </div>
        );
      })}
      {containers.every((c) => c.tasks.length === 0) && sections.length === 0 && (
        <EmptyState text={project.inbox_project ? t("emptyInbox") : t("emptyProject")} />
      )}
      <AddSection projectId={project.id} orderKey={lastKey ? keyBetween(lastKey, undefined) : undefined} always />
      <DragOverlay>{dragging ? <TaskRow item={dragging} /> : null}</DragOverlay>
    </DndContext>
  );
}
